import { createContext, useContext } from 'react';
import { useLocation, Navigate, Outlet } from 'react-router';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';

import { useAuth } from '@contexts/User.context';
import { createCan } from '@utils/entitlements';

const Loader = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 80vh;
	width: 100%;
	color: ${props => props.theme.colors.textColor};
`;

// Pages that read only the v2 subscription_state, never the v1 subscription
const SUBSCRIPTION_STATE_PATHS = ['/checkout', '/subscription'];

// Holds the page without adding a layout box, so it can be made inert and
// keyed without changing how the page lays out.
const Page = styled.div`
	display: contents;
`;

const SwitchingOverlay = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	padding: 30px;
	background-color: rgba(0, 0, 0, 0.8);
	color: white;
	z-index: 9999;
`;

// Set below the outermost guard, so a nested one (the Pro routes) leaves the
// switching overlay and remount to it.
const NestedGuard = createContext(false);

/**
 * Guards its child routes. Each `allowed*` prop is optional, and a user must
 * pass every one that is given: one of `allowedRoles`, one of `allowedTiers`,
 * and every key in `allowedEntitlements` (keys from ENTITLEMENTS).
 */
const RequireAuth = ({ allowedRoles, allowedTiers, allowedEntitlements }) => {
	const {
		currentUser,
		loading,
		switchingOrg,
		subscriptionLoading,
		tierPro,
		subscriptionActive,
		can,
	} = useAuth();
	const location = useLocation();
	const nested = useContext(NestedGuard);

	// Don't hold those pages back for the v1 subscription. The checkout page copies each
	// activation poll into the profile, so the first poll with a subscription_id starts
	// the v1 request, and this spinner would unmount the page mid-activation.
	const waitForSubscription =
		!!currentUser?.subscription_id &&
		subscriptionLoading &&
		!SUBSCRIPTION_STATE_PATHS.includes(location.pathname.replace(/\/$/, ''));

	if (loading || waitForSubscription)
		return (
			<Loader>
				<Spinner animation="border" role="status">
					<span className="visually-hidden">Loading page</span>
				</Spinner>
			</Loader>
		);

	// Used to restrict pro users that no longer have an active subscription
	const allowedPaths = ['/checkout', '/profile', '/projects/', '/subscription'];
	const currentPath = location.pathname;

	if (tierPro && !subscriptionActive && !allowedPaths.some(path => currentPath.includes(path))) {
		return <Navigate to="/" state={{ from: location }} replace />;
	}

	// `can()` grants nothing while the organization switches, but the profile
	// still describes the context being left until the new one arrives, so check
	// against that instead.
	const check = switchingOrg ? createCan(currentUser?.entitlements) : can;
	const entitled = !allowedEntitlements || allowedEntitlements.every(key => check(key));

	// Not entitled in the context being left, for example a gated link followed
	// mid-switch: don't mount the page (it would fire its queries), and don't
	// redirect yet, because the new context may grant it. A nested guard renders
	// nothing, since the outermost one already shows the switching overlay.
	if (switchingOrg && !entitled)
		return nested ? null : (
			<Loader>
				<Spinner animation="border" role="status">
					<span className="visually-hidden">Switching organization</span>
				</Spinner>
			</Loader>
		);

	// Check if the user is allowed to access the route based on their role, tier and entitlements.
	return (allowedRoles ? allowedRoles.includes(currentUser?.role) : true) &&
		(allowedTiers ? allowedTiers.includes(currentUser?.tier) : true) &&
		entitled ? (
		nested ? (
			<Outlet />
		) : (
			// While the organization switches, the profile (and so every check
			// above) still describes the previous context, so the page stays as it
			// was but cannot be used. It stays mounted, so a failed switch loses
			// nothing; a successful one remounts it for the new context.
			<NestedGuard.Provider value>
				<Page
					key={currentUser?.active_org_id ?? 'personal'}
					inert={switchingOrg}
					aria-busy={switchingOrg}
				>
					<Outlet />
				</Page>
				{switchingOrg && (
					<SwitchingOverlay>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Switching organization</span>
						</Spinner>
					</SwitchingOverlay>
				)}
			</NestedGuard.Provider>
		)
	) : currentUser?.loggedIn ? (
		<Navigate to="/unauthorized" state={{ from: location }} replace />
	) : (
		<Navigate to="/login" state={{ from: location }} replace />
	);
};

export default RequireAuth;
